/**
 * MathBase provides a common math API.
 */
export class MathBase {

    public static toFloatString(number: number, precision: number = 4): string {
        precision = Math.pow(10, precision);
        return (Math.round(number * precision) / precision).toString();
    }

    /**
     * Returns the highest value of all passed arguments like <code>Math.max( )</code>
     * but supports any number of args passed to it.
     */
    public static max(...args): number {
        return MathBase.maxArray(args);
    }

    /**
     * Returns the lowest value of all passed arguments like <code>Math.min( )</code>
     * but supports any number of args passed to it.
     */
    public static min(...args): number {
        return MathBase.minArray(args);
    }

    /**
     * Returns the highest value of all items in array like <code>Math.max( )</code>
     * but supports any number of items.
     */
    public static maxArray(a: any[]): number {
        return Math.max.apply(MathBase, a);
    }

    /**
     * Returns the lowest value of all items in array like <code>Math.min()</code>
     * but supports any number of items.
     */
    public static minArray(a: any[]): number {
        return Math.min.apply(MathBase, a);
    }

    /**
     * Same as <code>Math.floor( )</code> with extra argument to specify number of decimals.
     */
    public static floor(val: number, decimal: number): number {
        let n: number = Math.pow(10, decimal);
        return Math.floor(val * n) / n;
    }

    /**
     * Same as <code>Math.ceil( )</code> with extra argument to specify number of decimals.
     */
    public static ceil(val: number, decimal: number): number {
        let n: number = Math.pow(10, decimal);
        return Math.ceil(val * n) / n;
    }

    /**
     * Round to a given amount of decimals.
     */
    public static round(val: number, decimal: number): number {
        return Math.round(val * Math.pow(10, decimal)) / Math.pow(10, decimal);
    }

    /**
     * Returns a random number inside a specific range.
     */
    public static random(start: number, end: number, wantRound: boolean = true): number {
        if (wantRound) {
            return Math.round(Math.random() * (end - start)) + start;
        }
        else {
            return Math.random() * (end - start) + start;
        }
    }

    /**
     * Constrain a value to the defined numeric boundaries.
     * <i>Deprecated:</i> Use MathBase.clamp() instead.
     * @see #clamp()
     */
    public static constrain(val: number, min: number, max: number): number {
        if (val < min) {
            val = min;
        }
        else if (val > max) {
            val = max;
        }
        return val;
    }

    /**
     * Clamp constrains a value to the defined numeric boundaries.

     */
    public static clamp(val: number, min: number, max: number): number {
        if (val < min) {
            return min;
        }

        if (val > max) {
            return max;
        }
        return val;
    }

    /**
     * Similar to clamp and constrain but allows for <i>limit value wrapping</i>.
     * @see #clamp()
     */
    public static limit(val: number, min: number, max: number, wrap: boolean = false): number {
        if (!wrap) {
            return MathBase.clamp(val, min, max);
        }
        while (val > max)
            val -= (max - min);

        while (val < min)
            val += (max - min);
        return val;
    }

    /**
     * Return the distance between two points.
     */
    public static distance(x1: number, x2: number, y1: number, y2: number): number {
        let dx: number = x1 - x2;
        let dy: number = y1 - y2;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Return the proportional value of two pairs of numbers.
     */
    public static proportion(x1: number, x2: number, y1: number, y2: number, x: number = 1): number {
        let n: number = (!x) ? 1 : x;
        let slope: number = (y2 - y1) / (x2 - x1);
        return (slope * (n - x1) + y1);
    }

    /**
     * Return a percentage based on the numerator and denominator.
     */
    public static percent(amount: number, total: number): number {
        if (total == 0) {
            return 0;
        }
        return (amount / total * 100);
    }

    /**
     * Check if number is positive (zero is considered positive).
     */
    public static isPositive(n: number): boolean {
        return (n >= 0);
    }

    /**
     * Check if number is negative.
     */
    public static isNegative(n: number): boolean {
        return (n < 0);
    }

    /**
     * Check if number is Odd (convert to Integer if necessary).
     */
    public static isOdd(n: number): boolean {
        return (~~n) % 2 === 1;
    }

    /**
     * Check if number is Even (convert to Integer if necessary).
     */
    public static isEven(n: number): boolean {
        return ((~~n) % 2 === 0);
    }

    /**
     * Check if number is Prime (divisible only itself and one).
     */
    public static isPrime(n: number): boolean {
        if (n > 2 && n % 2 == 0) {
            return false;
        }
        let l: number = Math.sqrt(n);

        for (let i: number = 3; i <= l; i += 2) {
            if (n % i == 0) {
                return false;
            }
        }
        return true;
    }

    /**
     * Calculate the factorial of the integer.
     */
    public static factorial(n: number): number {
        if (n == 0) {
            return 1;
        }
        let d: number = n.valueOf();
        let i: number = d - 1;

        while (i) {
            d = d * i;
            i--;
        }
        return d;
    }

    /**
     * Return an array of divisors of the integer.
     */
    public static getDivisors(n: number): any[] {
        let r: any[] = new Array();

        for (let i: number = 1, e: number = n / 2; i <= e; i++) {
            if (n % i == 0) {
                r.push(i);
            }
        }

        if (n != 0) {
            r.push(n.valueOf());
        }
        return r;
    }

    /**
     * Check if number is an Integer.
     */
    public static isInteger(n: number): boolean {
        return (n % 1 == 0);
    }

    /**
     * Check if number is Natural (positive Integer).
     */
    public static isNatural(n: number): boolean {
        return (n >= 0 && n % 1 == 0);
    }

    /**
     * Correct "roundoff errors" in floating point math.
     * @see http://www.zeuslabs.us/2007/01/30/flash-floating-point-number-errors/
     */
    public static sanitizeFloat(n: number, precision: number = 5): number {
        let c: number = Math.pow(10, precision);
        return Math.round(c * n) / c;
    }

    /**
     * Evaluate if two numbers are nearly equal.
     * @see http://www.zeuslabs.us/2007/01/30/flash-floating-point-number-errors/
     */
    public static fuzzyEqual(n1: number, n2: number, precision: number = 5): boolean {
        let d: number = n1 - n2;
        let r: number = Math.pow(10, -precision);
        return d < r && d > -r;
    }

    public static getSign(value: number): number {
        if (value < 0) {
            return -1;
        }
        return 1;
    }

    /**
     * MathBase Static Constructor
     */
    constructor() {
        throw new Error("MathBase is a static class and cannot be instantiated.");
    }
}
